import { useEffect, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { DialogoAlerta } from "@/components/alert-dialog";
import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useGlobalLoader } from "@/components/GlobalLoader";

import { useFilaImpressaoSocket } from "@/contexts/FilaImpressaoSocketContext";
import type { ItemFilaSocketResponse } from "@/types/item-fila-socket-response";

import {
  CheckCircle2,
  Inbox,
  RefreshCw,
  Trash2,
} from "lucide-react";

function FilaImpressaoSocket() {
  const { isLoading } = useGlobalLoader();
  const { coletarItemFila, itensFila, apagarFilaImpressao } =
    useFilaImpressaoSocket();
  const [isOpenAlertDialog, setIsOpenAlertDialog] = useState(false);

  const columns: ColumnDef<ItemFilaSocketResponse>[] = [
    {
      id: "posicao",
      header: () => <div className="w-12">#</div>,
      cell: ({ row }) => (
        <div className="flex h-6 w-6 items-center justify-center rounded bg-blue-500/10 text-blue-700 dark:text-blue-400 text-[11px] font-medium">
          {row.index + 1}
        </div>
      ),
    },
    {
      accessorKey: "nome_da_ordem",
      header: "Nome da ordem",
      cell: ({ row }) => (
        <span className="text-sm font-medium">{row.original.nome_da_ordem}</span>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Ações</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex items-center justify-end">
            <Button
              disabled={isLoading}
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
              onClick={() => apagarFilaImpressao(item.nome_da_ordem)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Cancelar
            </Button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    coletarItemFila();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <DialogoAlerta
        description="Tem certeza que deseja limpar toda a fila de impressão? Todas as ordens pendentes serão canceladas. Esta ação não pode ser desfeita."
        title="Limpar fila de impressão"
        isOpen={isOpenAlertDialog}
        onConfirm={() => apagarFilaImpressao("")}
        onOpenChange={() => setIsOpenAlertDialog(false)}
        key="dialogo-alerta-limpar-fila-impressao"
      />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Fila de Impressão
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Ordens aguardando impressão no hardware conectado
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => coletarItemFila()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>

          <Button
            variant="destructive"
            size="sm"
            onClick={() => setIsOpenAlertDialog(true)}
            disabled={isLoading || itensFila.length === 0}
            title={
              itensFila.length === 0
                ? "A fila já está vazia"
                : "Cancelar todas as ordens pendentes"
            }
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Limpar fila</span>
          </Button>
        </div>
      </div>

      {/* Card com fila */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">
                Itens na fila
              </CardTitle>
            </div>
            <Badge variant="secondary" className="font-medium">
              {itensFila.length}{" "}
              {itensFila.length === 1 ? "pendente" : "pendentes"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {itensFila.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-medium text-foreground">
                  Fila vazia
                </p>
                <p className="text-xs">
                  Nenhuma ordem aguardando impressão no momento. Quando uma
                  ordem for enviada, ela aparecerá aqui.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => coletarItemFila()}
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
                Atualizar
              </Button>
            </div>
          ) : (
            <DataTable columns={columns} data={itensFila} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FilaImpressaoSocket;
